import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Sparkles, Bot, Filter, Users, Check, Play, MousePointer2, Clock, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export const HowManagedCampaignsWorkPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="h-screen w-full bg-white dark:bg-zinc-950 relative overflow-hidden flex flex-col font-sans selection:bg-primary/20">
      {/* Nav - Fixed Top */}
      <div className="absolute top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground gap-2 hover:bg-muted/50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10 shadow-sm backdrop-blur-md">
          <Bot className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-primary/90">Automatic Campaigns</span>
        </div>
      </div>

      {/* Main Content - Vertically Centered */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-20 w-full max-w-[1400px] mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-16 space-y-6">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.1]">
            Growth on <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-600 to-blue-600">Autopilot</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-medium">
            We handle the heavy lifting. You focus on closing.
          </p>
        </div>

        {/* The Pipeline Visual */}
        <div className="w-full relative max-w-7xl mx-auto">
          {/* Connecting Line */}
          <div className="absolute top-[60px] left-16 right-16 h-px border-t-2 border-dashed border-primary/10 hidden md:block" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-10 relative">

            {/* Step 1: Target - Filter Builder UI */}
            <ProcessStep
              title="Target"
              description="Define your Ideal Customer Profile. We handle the rest."
              stepNumber="01"
              delay={0}
            >
              <div className="absolute inset-0 flex items-center justify-center p-5">
                <div className="w-full h-full bg-white dark:bg-zinc-900 border border-border shadow-sm rounded-xl flex flex-col p-3 space-y-3 relative overflow-hidden group-hover:border-primary/20 transition-colors">
                  <div className="flex items-center gap-2 border-b border-border/50 pb-2">
                    <div className="p-1 px-1.5 bg-muted rounded flex items-center gap-1.5">
                      <Filter className="w-3 h-3 text-muted-foreground" />
                      <span className="text-[10px] font-semibold text-muted-foreground">Filters</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap content-start gap-2">
                    <FilterChip color="bg-blue-50 text-blue-600 border-blue-100" label="CEO" delay={0} />
                    <FilterChip color="bg-purple-50 text-purple-600 border-purple-100" label="SaaS" delay={150} />
                    <FilterChip color="bg-emerald-50 text-emerald-600 border-emerald-100" label="USA" delay={300} />
                  </div>
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-primary/5 to-transparent rounded-tr-xl" />
                </div>
              </div>
            </ProcessStep>

            {/* Step 2: Source - Lead List UI */}
            <ProcessStep
              title="Source"
              description="AI finds perfect matches from millions of profiles."
              stepNumber="02"
              delay={100}
            >
              <div className="absolute inset-0 flex items-center justify-center p-5">
                <div className="w-full h-full bg-white dark:bg-zinc-900 border border-border shadow-sm rounded-xl flex flex-col overflow-hidden relative group-hover:border-primary/20 transition-colors">
                  <div className="h-8 bg-muted/30 border-b border-border/50 flex items-center px-3 gap-2">
                    <div className="w-20 h-2 bg-muted-foreground/10 rounded-full" />
                    <div className="ml-auto w-12 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-[8px] font-bold text-primary animate-pulse">Scanning</span>
                    </div>
                  </div>
                  <div className="p-3 space-y-3">
                    <div className="flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-700">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 border border-blue-100 shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="w-full h-2 bg-muted rounded-full" />
                        <div className="w-2/3 h-2 bg-muted/60 rounded-full" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-700 delay-150">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-50 border border-purple-100 shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="w-full h-2 bg-muted rounded-full" />
                        <div className="w-2/3 h-2 bg-muted/60 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-white shadow-lg shadow-primary/30 animate-bounce">
                  5
                </div>
              </div>
            </ProcessStep>

            {/* Step 3: Verify - Review Interface UI */}
            <ProcessStep
              title="Verify"
              description="Humans manually verify every single lead."
              stepNumber="03"
              delay={200}
            >
              <div className="absolute inset-0 flex items-center justify-center p-2">
                <div className="relative bg-white dark:bg-zinc-900 border border-border shadow-md rounded-xl p-4 w-[85%] flex items-center gap-3 group-hover:border-primary/20 transition-colors">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center text-orange-600 font-bold text-xs ring-2 ring-white dark:ring-zinc-900">MK</div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white dark:border-zinc-900 rounded-full flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="w-20 h-2.5 bg-foreground/80 rounded-full mb-1.5" />
                    <div className="w-14 h-2 bg-muted-foreground/30 rounded-full" />
                  </div>

                  {/* Approve Button */}
                  <div className="h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold rounded-lg shadow-sm flex items-center transition-transform active:scale-95 cursor-default">
                    Approve
                  </div>

                  {/* Cursor */}
                  <div className="absolute bottom-[-10px] right-[20px] drop-shadow-lg animate-[cursorMove_3s_ease-in-out_infinite]">
                    <MousePointer2 className="w-5 h-5 fill-black text-white" />
                  </div>
                </div>
              </div>
            </ProcessStep>

            {/* Step 4: Launch - Sequence Timeline UI */}
            <ProcessStep
              title="Launch"
              description="Campaigns run automatically. You just reply."
              stepNumber="04"
              delay={300}
            >
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="flex items-center w-full justify-between px-2 relative">
                  {/* Track Line */}
                  <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-muted -z-10" />
                  <div className="absolute top-1/2 left-4 w-1/2 h-0.5 bg-emerald-500/30 -z-10 animate-[growWidth_3s_ease-in-out_infinite]" />

                  {/* Step 1: Mail */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-600 flex items-center justify-center shadow-sm z-10 transition-transform hover:scale-110">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="text-[9px] font-bold text-foreground/80">Msg 1</span>
                  </div>

                  {/* Step 2: Wait */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-background border border-border text-muted-foreground flex items-center justify-center shadow-sm z-10">
                      <Clock className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-[8px] font-bold bg-muted px-1.5 py-0.5 rounded text-muted-foreground mt-1">2 Day</div>
                  </div>

                  {/* Step 3: Follow Up */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-background border border-border text-muted-foreground flex items-center justify-center shadow-sm z-10">
                      <Mail className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[9px] font-medium text-muted-foreground">Msg 2</span>
                  </div>
                </div>
              </div>
            </ProcessStep>

          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center animate-in fade-in slide-in-from-bottom-8 duration-700">
          <Button
            onClick={() => navigate(-1)}
            size="lg"
            className="h-14 px-10 rounded-full text-lg font-semibold shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all hover:-translate-y-1 bg-primary text-primary-foreground border-0 gap-2 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            <span className="relative flex items-center gap-2">
              Start Your Campaign
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </Button>
          <p className="mt-4 text-sm font-medium text-muted-foreground/80">
            Takes only 2 min
          </p>
        </div>

      </div>

      <style>{`
        @keyframes cursorMove {
          0%, 100% { transform: translate(10px, 10px); }
          40% { transform: translate(0, 0); }
          50% { transform: scale(0.9); }
          60% { transform: scale(1); }
          80% { transform: translate(10px, 10px); }
        }
        @keyframes growWidth {
           0% { width: 0%; }
           50% { width: 50%; }
           100% { width: 50%; }
        }
      `}</style>
    </div>
  );
};

/* Helper Components */

const FilterChip = ({ label, color, delay }: { label: string, color: string, delay: number }) => (
  <div
    className={cn("h-5 px-2.5 rounded-full text-[9px] font-bold border flex items-center shadow-sm animate-in fade-in slide-in-from-left-1 duration-500 fill-mode-backwards", color)}
    style={{ animationDelay: `${delay}ms` }}
  >
    {label}
  </div>
);

const ProcessStep = ({
  title,
  description,
  children,
  delay,
  stepNumber
}: {
  title: string,
  description: string,
  children: React.ReactNode,
  delay: number,
  stepNumber: string
}) => {
  return (
    <div
      className="relative flex flex-col items-center text-center group"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Visual Window - Highlighted for emphasis */}
      <div className="w-full h-32 bg-white dark:bg-zinc-900 border-2 border-primary/5 rounded-2xl mb-6 relative overflow-visible shadow-lg shadow-primary/5 group-hover:shadow-xl group-hover:border-primary/30 transition-all duration-300 z-10">

        {/* Step Number Badge */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 bg-white dark:bg-zinc-900 text-[9px] font-bold text-primary tracking-widest px-3 py-1 rounded-full border border-primary/20 shadow-sm">
          STEP {stepNumber}
        </div>

        {children}
      </div>

      {/* Content */}
      <div className="flex flex-col items-center relative z-10 px-1 max-w-[260px] mx-auto space-y-2">
        <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed font-medium text-balance">
          {description}
        </p>
      </div>
    </div>
  );
};
